/**
 * Button + ButtonLink (design-system.md §12 press feedback, §14 disabled state).
 * `Button` defaults to `type="button"` so a stray button inside a form never submits it.
 * `ButtonLink` is a plain `<a>` styled as a button — downloads, external links, the client
 * portal's signed-URL redirects. It is router-free by design: apps/web wraps it in TanStack's
 * `createLink` as `LinkButton` (type-safe `to`), see apps/web/src/components/ui/button.tsx.
 */
import { Loader2 } from './icons.ts';
import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonStyleProps {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  /** Leading icon (a 16px Solar element from icons.ts). */
  icon?: ReactNode | undefined;
  /** Square icon-only button — pass `aria-label`. */
  iconOnly?: boolean | undefined;
}

export function buttonClassName(
  { variant = 'secondary', size = 'md', iconOnly = false }: ButtonStyleProps,
  extra?: string,
): string {
  return [
    'argus-btn',
    `argus-btn--${variant}`,
    size !== 'md' ? `argus-btn--${size}` : '',
    iconOnly ? 'argus-btn--icon' : '',
    extra ?? '',
  ]
    .filter(Boolean)
    .join(' ');
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, ButtonStyleProps {
  /** Shows a spinner, sets aria-busy and disables the button. */
  loading?: boolean | undefined;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant, size, icon, iconOnly, loading = false, className, children, disabled, type, ...rest },
  ref,
) {
  const iconSize = size === 'sm' ? 14 : 16;
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={buttonClassName({ variant, size, iconOnly }, className)}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <Loader2 className="argus-btn__spinner" size={iconSize} aria-hidden="true" />
      ) : (
        icon
      )}
      {children}
    </button>
  );
});

export type ButtonLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & ButtonStyleProps;

/** A plain `<a>` that looks like a `<Button>`; `href` is a URL, not a route. */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant, size, icon, iconOnly, className, children, ...rest },
  ref,
) {
  return (
    <a ref={ref} className={buttonClassName({ variant, size, iconOnly }, className)} {...rest}>
      {icon}
      {children}
    </a>
  );
});
